/**
 * Die **Modulordnung** der Sidebar: welcher Bereich dieses Werkzeugs auf welchem
 * Modulplatz der Vorlage sitzt.
 *
 * `FRAME.md` erklärt unter „Sidebar" **Position, Reihenfolge und Glyphe** der
 * neunzehn Module aus `reference/AppShell.jsx` (`MENU`) zum Rahmen: Ein Produkt,
 * das nur einen Teil davon hat, **streicht** Module — es sortiert sie nicht um und
 * erfindet keine dazwischen. Frei ist allein die **Beschriftung**: Wer seine
 * Vorgänge nicht `Projekte` nennt, schreibt sein eigenes Wort in die Zeile.
 *
 * Vier Plätze sind hier belegt, fünfzehn gestrichen:
 *
 * | Platz | Modul der Vorlage | Glyphe | Wort hier |
 * | --- | --- | --- | --- |
 * | 2 | Stammdaten | `layers` | **Revisionen** |
 * | 13 | Controlling | `trending-up` | **Auswertung** |
 * | 17 | Werkzeuge | `wrench` | **Gerüst** |
 * | 19 | Einstellungen | `settings` | **Verwaltung** |
 *
 * **Warum diese vier.** Platz 2 trägt in der Vorlage die Stammdaten — das, womit
 * gearbeitet wird; hier sind das die Revisionen, ihre Befunde und der Bestand, und
 * `layers` ist die Glyphe, die der Revisionsstrom schon trug. Platz 13 ist das
 * Controlling: die Zahlen, an denen abgelesen wird, ob etwas gebaut werden soll —
 * Abweichung und Nachhalten, und für dieselben Rollen wie dort. Platz 17 sind die
 * Werkzeuge, und das Gerüst ist genau das: der Blick auf die eigene Installation.
 * Platz 19 sind die Einstellungen, und die Verwaltung ist die Einstellung dieses
 * Werkzeugs. Die fünf heutigen Glyphen `list-checks`, `scan-search`, `gauge`,
 * `flag` und `hard-drive` fallen mit den gestrichenen Modulen weg: Auf einem
 * Modulplatz steht die Glyphe der Vorlage.
 *
 * **Die Rollenfilterung ist eine Frage der Anzeige und keine Rechteentscheidung.**
 * Die trifft der Server bei jeder Anfrage — `/api/admin/*` nur für `admin`,
 * `/api/deviation` für `management` und `admin`. Neu gegenüber der flachen Leiste
 * ist allein die Regel für die zweite Stufe: **Eine Gruppe erscheint, wenn
 * mindestens einer ihrer Untereinträge erscheint**, und fällt sonst ganz weg. Ein
 * Platz 13, der für eine reviewende Person leer dastünde, wäre eine Auskunft über
 * Rechte, die niemand gegeben hat.
 */

import type { Role } from "../api/client";
import type { RailModule } from "../shell/NavRail";
import type { Area } from "./place";

/** Ein Bereich, wie das Menü ihn anbietet. */
export interface ModuleArea {
  id: Area;
  /** Das Wort im Menü. */
  label: string;
  /**
   * Das Wort in der **Brotkrume**, wo es ein anderes ist als im Menü.
   *
   * Die Brotkrume ist der Titel des Screens und nicht der Weg dorthin: Der
   * Revisionsstrom heißt im Menü so, und die Ansicht selbst nennt sich
   * „Revisionen". Bei allen übrigen Bereichen fallen beide Wörter zusammen, und
   * dann steht hier nichts.
   */
  crumb?: string;
  /** Die Rollen, denen der Eintrag angeboten wird; `null` heißt allen. */
  roles: readonly Role[] | null;
}

/** Ein Modulplatz der Vorlage, belegt mit den Bereichen dieses Werkzeugs. */
export interface ModulePlace {
  /** Der Platz in der Modulordnung der Vorlage, von eins gezählt. */
  place: number;
  /** Das Modul der Vorlage, dessen Platz und Glyphe dieser Eintrag übernimmt. */
  template: string;
  /** Die Glyphe der Vorlage — fest, nicht austauschbar. */
  glyph: string;
  /** Das Wort hier — frei. */
  label: string;
  /**
   * Die Bereiche dieses Platzes.
   *
   * **Zweistufig** (`nested`) ist jeder davon ein Untereintrag. **Einstufig** ist
   * es genau einer, und der Platz selbst führt in ihn — dann gibt es keine zweite
   * Stufe.
   */
  areas: readonly ModuleArea[];
  nested: boolean;
}

/** Die Rollen, die eine Zahl lesen dürfen, an der abgelesen wird, ob gebaut wird. */
const NUMBERS: readonly Role[] = ["management", "admin"];

export const MODULES: readonly ModulePlace[] = [
  {
    place: 2,
    template: "Stammdaten",
    glyph: "layers",
    label: "Revisionen",
    nested: true,
    areas: [
      // Der Revisionsstrom steht oben und ist der Anfang: Er ist die erste
      // Ansicht, die ein Entwickler öffnet.
      { id: "revisions", label: "Revisionsstrom", crumb: "Revisionen", roles: null },
      { id: "sites", label: "Befunde", roles: null },
      // Der **Bestand** steht daneben und nicht im Strom: Die Befunde eines
      // Bestandslaufs hängen alle an derselben Revision und wären dort nicht
      // auffindbar.
      { id: "inventory", label: "Bestand", roles: null },
    ],
  },
  {
    place: 13,
    template: "Controlling",
    glyph: "trending-up",
    label: "Auswertung",
    nested: true,
    areas: [
      { id: "deviation", label: "Abweichung", roles: NUMBERS },
      // **Nachhalten** (ADR-0012): der Rückstand, mit dem v2 an Tag eins arbeitet.
      { id: "follow-up-marks", label: "Nachhalten", roles: NUMBERS },
    ],
  },
  {
    place: 17,
    template: "Werkzeuge",
    glyph: "wrench",
    label: "Gerüst",
    nested: false,
    areas: [{ id: "scaffold", label: "Gerüst", roles: null }],
  },
  {
    place: 19,
    template: "Einstellungen",
    glyph: "settings",
    label: "Verwaltung",
    nested: false,
    // Die Verwaltung bleibt einstufig: Ihre acht Abschnitte — Endpunkte,
    // Basismodelle, Agenten, Projekte, Personen, SVN-Kennungen, Ticketsystem,
    // Prüfaufträge — wählt ihre Werkzeugleiste und nicht die Sidebar (#41).
    areas: [{ id: "administration", label: "Verwaltung", roles: ["admin"] }],
  },
];

/**
 * Alle Bereiche, ohne Rücksicht auf die Rolle — in der Reihenfolge des Menüs.
 *
 * Daraus liest die **Brotkrume** ihr Wort: Sie nennt den Ort, an dem man steht, und
 * an dem steht man nur, wenn die Rolle ihn hergibt.
 */
export const AREAS: readonly ModuleArea[] = MODULES.flatMap((one) => one.areas);

/** Sieht diese Rolle diesen Eintrag? */
const offered = (area: ModuleArea, role: Role): boolean =>
  area.roles === null || area.roles.includes(role);

/**
 * Die Bereiche, die diese Rolle sieht — flach und in der Reihenfolge des Menüs.
 *
 * Daran hängt der Rückfall: Eine Adresse, die der eigenen Rolle nicht offensteht,
 * zeigt den Revisionsstrom.
 */
export function areasFor(role: Role): ModuleArea[] {
  return AREAS.filter((area) => offered(area, role));
}

/**
 * Das Menü, wie die Spalte es bekommt: die Plätze dieser Rolle mit ihren sichtbaren
 * Einträgen.
 *
 * Ein Platz ohne sichtbaren Eintrag fällt ganz weg — auch der einstufige, dessen
 * einziger Eintrag er selbst ist.
 */
export function railModules(role: Role): RailModule<Area>[] {
  return MODULES.flatMap((one) => {
    const areas = one.areas.filter((area) => offered(area, role));
    const own = areas[0];

    if (own === undefined) {
      return [];
    }

    return [
      {
        label: one.label,
        glyph: one.glyph,
        areas: one.nested ? areas.map(({ id, label }) => ({ id, label })) : [],
        area: one.nested ? null : own.id,
      },
    ];
  });
}
